import {assert} from 'Asserts';

import {getFieldInfoFromDataFlowEdgeHandle} from '../../flowchartIdentifierUtils';
import {
  CustomMappingData,
  FlowchartStaticEdgeData,
  ProcessFlowchartEdgeType,
  TransitionSource,
} from '../../processFlowchartConfigEditorConstants';
import {
  StaticFlowchartNodeData,
  isStaticTaskSchemaNodeData,
} from '../../processFlowchartConfigEditorNodeTypes';
import {TransitionChange} from '../../processFlowchartConfigEditorUtils';
import {getNodeIdPairFromTransitionSourceKey} from '../../TransitionSourceUtils';
import {getFieldPairKey, getTransitionId} from './transitionKeyUtils';
import {findNodePairByIds, findTaskSchemaNodePair, getPTDVIdFromNode} from './transitionLookupUtils';
import {Transition} from './TransitionTypes';

export interface CreateTransitionChangeForCustomMappingParams {
  edgeId: string;
  /** Pass null to create a change that removes the custom mapping for this edge's field pair. */
  customMappingData: CustomMappingData | null;
  edges: Array<FlowchartStaticEdgeData>;
  nodes: Array<StaticFlowchartNodeData>;
  transitions: Record<string, Transition>;
}

export function createTransitionChangeForCustomMapping({
  edgeId,
  customMappingData,
  edges,
  nodes,
  transitions,
}: CreateTransitionChangeForCustomMappingParams): TransitionChange | null {
  const edge = edges.find((e) => e.id === edgeId);
  if (!edge || edge.type !== ProcessFlowchartEdgeType.DATA_FLOW) {
    return null;
  }

  const nodePair = findTaskSchemaNodePair(edge, nodes);
  if (!nodePair) {
    return null;
  }

  const sourcePTDVId = getPTDVIdFromNode(nodePair.sourceNode);
  const targetPTDVId = getPTDVIdFromNode(nodePair.targetNode);
  const transitionId = getTransitionId([sourcePTDVId], targetPTDVId);

  const sourceHandle = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle);
  const targetHandle = getFieldInfoFromDataFlowEdgeHandle(edge.targetHandle);

  const fieldPairKey = getFieldPairKey({
    sourcePTDVId,
    sourceSectionType: sourceHandle.sectionType,
    sourceFieldClientId: sourceHandle.fieldClientId,
    targetPTDVId,
    targetSectionType: targetHandle.sectionType,
    targetFieldClientId: targetHandle.fieldClientId,
  });

  assert(transitionId != null, 'Transition ID should not be null');

  return {
    type: 'update',
    id: transitionId,
    changes: {
      customMapping: {fieldPairKey, value: customMappingData},
    },
  };
}

export interface CreateTransitionChangeForTransitionSourceParams {
  nodeIdPair: string;
  transitionSource: TransitionSource;
  nodes: Array<StaticFlowchartNodeData>;
  transitions: Record<string, Transition>;
}

export function createTransitionChangeForTransitionSource({
  nodeIdPair,
  transitionSource,
  nodes,
  transitions,
}: CreateTransitionChangeForTransitionSourceParams): TransitionChange | null {
  const {sourceNodeId, targetNodeId} = getNodeIdPairFromTransitionSourceKey(nodeIdPair);

  const nodePair = findNodePairByIds(sourceNodeId, targetNodeId, nodes);
  if (!nodePair) {
    return null;
  }
  if (!isStaticTaskSchemaNodeData(nodePair.sourceNode) || !isStaticTaskSchemaNodeData(nodePair.targetNode)) {
    return null;
  }

  const sourcePTDVId = getPTDVIdFromNode(nodePair.sourceNode);
  const targetPTDVId = getPTDVIdFromNode(nodePair.targetNode);
  const transitionId = getTransitionId([sourcePTDVId], targetPTDVId);

  assert(transitionId != null, 'Transition ID should not be null');

  return {
    type: 'update',
    id: transitionId,
    changes: {
      transitionSource: {sourcePTDVId, value: transitionSource},
    },
  };
}
